import { ScrollReveal } from '@/components/ui/scroll-reveal';
import { useDocumentHead } from '@/hooks/use-document-head';

const PROTOCOLS = [
  { title: 'EPA-REGISTERED DISINFECTANTS', desc: 'Hospital-grade, EPA List N disinfectants effective against SARS-CoV-2, influenza, MRSA, and more.' },
  { title: 'CROSS-CONTAMINATION PREVENTION', desc: 'Color-coded microfiber system. Clean to dirty workflow. Separate tools for exam rooms vs. common areas.' },
  { title: 'OSHA COMPLIANCE', desc: 'Bloodborne pathogen protocols. Proper PPE usage. Sharps awareness. Biohazard waste handling procedures.' },
  { title: 'HIPAA-AWARE HANDLING', desc: 'Crews trained to never read, move, or disturb patient records. Document security is part of the clean.' },
];

const FACILITY_TYPES = [
  { icon: '🏥', name: 'Medical Offices' },
  { icon: '🦷', name: 'Dental Practices' },
  { icon: '👁️', name: 'Optometry Clinics' },
  { icon: '🏋️', name: 'Physical Therapy' },
  { icon: '💆', name: 'Dermatology' },
  { icon: '🧬', name: 'Laboratories' },
  { icon: '🐾', name: 'Veterinary Clinics' },
  { icon: '💊', name: 'Pharmacies' },
];

const AREAS_CLEANED = [
  'Waiting rooms & reception',
  'Exam rooms & treatment areas',
  'Restrooms & patient bathrooms',
  'Break rooms & staff areas',
  'Hallways & common areas',
  'Lab spaces & equipment surfaces',
];

export default function MedicalPage() {
  useDocumentHead(
    'Medical Facility Cleaning in Orlando, FL | SOTSVC',
    'Medical office & facility cleaning across Orlando & Central Florida by Sonz of Thunder Services (SOTSVC). Sanitized & reliable.',
  );
  return (
    <div className="min-h-screen">
      {/* Hero */}
      <section className="relative h-[60vh] min-h-[500px] clip-diag-bottom overflow-hidden">
        <img src="/images/pages/medical-procedure.png" alt="" className="absolute inset-0 w-full h-full object-cover ken-burns" />
        <div className="absolute inset-0" style={{ background: 'linear-gradient(135deg, rgba(15,15,30,.92) 0%, rgba(15,15,30,.6) 50%, rgba(0,180,216,.2) 100%)' }} />
        <div className="absolute inset-0 opacity-20 grid-pattern" />
        <div className="relative z-10 h-full flex items-center">
          <div className="max-w-7xl mx-auto px-6 lg:px-10 w-full fade-in">
            <div className="flex items-center gap-4 mb-6">
              <span className="h-[3px] w-16 bg-thunder" />
              <span className="text-thunder font-display tracking-[0.4em] text-xs">// HEALTH-BASED CLEANING</span>
            </div>
            <h1 className="font-display font-bold text-white text-shadow-bold leading-[0.88] mb-6">
              <span className="block text-4xl sm:text-6xl md:text-8xl">MEDICAL GRADE.</span>
              <span className="block text-5xl sm:text-7xl md:text-9xl text-thunder" style={{ textShadow: '0 0 60px rgba(255,214,10,.5)' }}>ZERO RISK.</span>
            </h1>
            <p className="text-white/80 text-lg sm:text-xl max-w-xl font-light">
              OSHA-aware. HIPAA-conscious. EPA-registered products. Because patient safety starts with the surfaces they touch.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 mt-10">
              <button
                onClick={() => window.dispatchEvent(new CustomEvent('openContactForm'))}
                className="pulse-btn bg-electric hover:bg-electric-bright text-charcoal-deep font-display font-bold px-10 py-5 text-sm tracking-[0.3em] transition glow-electric w-full sm:w-auto text-center"
              >
                GET MEDICAL QUOTE →
              </button>
              <a href="tel:4074616039" className="border-2 border-thunder text-thunder hover:bg-thunder hover:text-charcoal-deep font-display font-bold px-10 py-5 text-sm tracking-[0.3em] transition w-full sm:w-auto text-center">
                CALL DIRECT
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Protocols */}
      <section className="relative py-28 bg-charcoal-deep overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <ScrollReveal className="text-center mb-16">
            <div className="text-electric font-display tracking-[0.4em] text-xs mb-3">// COMPLIANCE PROTOCOLS</div>
            <h2 className="font-display font-bold text-white text-5xl sm:text-6xl lg:text-7xl leading-[0.9]">
              BEYOND <span className="text-thunder">CLEAN.</span>
            </h2>
            <p className="text-white/60 text-lg mt-6 max-w-2xl mx-auto">
              Medical cleaning isn't about appearance — it's about safety. We follow healthcare-grade protocols that protect patients, staff, and your practice.
            </p>
          </ScrollReveal>

          <div className="grid sm:grid-cols-2 gap-6">
            {PROTOCOLS.map((p, i) => (
              <ScrollReveal key={p.title} delay={i * 100}>
                <div className="border-l-4 border-electric bg-charcoal-mid/30 p-8 h-full">
                  <h3 className="font-display font-bold text-white text-xl mb-3 tracking-wider">{p.title}</h3>
                  <p className="text-white/60 leading-relaxed">{p.desc}</p>
                </div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* Facility Types */}
      <section className="relative py-28 bg-charcoal overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <ScrollReveal className="text-center mb-16">
            <div className="text-thunder font-display tracking-[0.4em] text-xs mb-3">// FACILITIES WE SERVE</div>
            <h2 className="font-display font-bold text-white text-5xl sm:text-6xl lg:text-7xl leading-[0.9]">
              EVERY <span className="text-electric">PRACTICE.</span> EVERY <span className="text-electric">SPECIALTY.</span>
            </h2>
          </ScrollReveal>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            {FACILITY_TYPES.map((f, i) => (
              <ScrollReveal key={f.name} delay={i * 60}>
                <div className="bg-charcoal-deep border border-electric/20 p-6 text-center hover:border-thunder/60 transition-all hover:-translate-y-1">
                  <div className="text-4xl mb-3">{f.icon}</div>
                  <div className="font-display text-white tracking-wider text-sm">{f.name}</div>
                </div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* What We Clean */}
      <section className="relative py-28 bg-charcoal-deep overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-2 gap-16 items-center">
          <ScrollReveal direction="left" className="relative">
            <div className="absolute -inset-4 bg-electric/10 blur-3xl" />
            <div className="relative overflow-hidden border-2 border-electric/40">
              <img src="/images/pages/medical-dental.png" alt="Dental office cleaning" className="w-full aspect-[4/3] object-cover" />
            </div>
          </ScrollReveal>

          <ScrollReveal direction="right">
            <div className="flex items-center gap-3 mb-4">
              <span className="h-[2px] w-10 bg-thunder" />
              <span className="text-thunder font-display tracking-[0.4em] text-xs">// SCOPE OF WORK</span>
            </div>
            <h2 className="font-display font-bold text-white text-5xl sm:text-6xl leading-[0.9] mb-8">
              WHAT WE <span className="text-electric">CLEAN</span>
            </h2>
            <ul className="space-y-4 mb-10">
              {AREAS_CLEANED.map((area) => (
                <li key={area} className="flex items-center gap-4 text-white/80 font-display tracking-wider text-sm">
                  <span className="text-electric text-xl">&#x25B8;</span>
                  <span>{area}</span>
                </li>
              ))}
            </ul>
            <button
              onClick={() => window.dispatchEvent(new CustomEvent('openContactForm'))}
              className="bg-thunder hover:bg-thunder-dark text-charcoal-deep font-display font-bold px-8 py-4 tracking-[0.25em] text-sm transition"
            >
              REQUEST WALKTHROUGH →
            </button>
          </ScrollReveal>
        </div>
      </section>

      {/* CTA */}
      <section className="relative py-32 overflow-hidden clip-diag-both">
        <img src="/images/pages/medical-lobby.png" alt="" className="absolute inset-0 w-full h-full object-cover ken-burns" />
        <div className="absolute inset-0" style={{ background: 'linear-gradient(180deg, rgba(15,15,30,.85) 0%, rgba(15,15,30,.6) 50%, rgba(15,15,30,.9) 100%)' }} />
        <ScrollReveal className="relative z-10 max-w-4xl mx-auto px-6 lg:px-10 text-center">
          <h2 className="font-display font-bold text-white text-5xl sm:text-6xl md:text-7xl leading-[0.88] mb-8">
            PATIENT SAFETY STARTS WITH <span className="text-thunder">CLEAN.</span>
          </h2>
          <p className="text-white/80 text-lg max-w-xl mx-auto mb-10">
            Free compliance-focused walkthrough. Custom cleaning plan built around your practice's schedule and requirements.
          </p>
          <button
            onClick={() => window.dispatchEvent(new CustomEvent('openContactForm'))}
            className="pulse-btn bg-electric hover:bg-electric-bright text-charcoal-deep font-display font-bold px-12 py-5 text-sm tracking-[0.3em] transition glow-electric"
          >
            SCHEDULE WALKTHROUGH →
          </button>
        </ScrollReveal>
      </section>
    </div>
  );
}
